import { useEffect, useRef, useState } from 'react';
import { QUICK_REACTIONS, reactionName } from '../comments/CommentReactions';
import { AnimatedEmoji } from '../comments/AnimatedEmoji';
import type { ReactionSummary } from '../comments/reactions';
import { PIN_FULL_HINT } from './pins';

/**
 * Drop 3c message actions (design-note-drop3c.md "What changes", "Three-dot
 * menu", "Phone"; mockup-drop3c.html menuHtml/sheetHtml).
 *
 * Desktop: a small bar on hover with Reply, Forward and three dots. Phone:
 * no hover, so a tap or long press opens a bottom sheet with the quick
 * reactions row and the same actions. The page passes null for a Viewer and
 * for a tombstone, and neither the bar nor the sheet renders.
 */
export interface MessageActionSet {
  /** Null on a thread reply: threads are one level deep (D1). */
  onReply: (() => void) | null;
  /** Null when the reader has nowhere to post (a Viewer never gets here). */
  onForward: (() => void) | null;
  onMakeTask: () => void;
  /** Null on a thread reply: pins are for room messages only. */
  pin: { pinned: boolean; full: boolean; onToggle: () => void } | null;
  /**
   * Null unless the reader wrote the message, and always null on a forwarded
   * copy: its body is someone else's words (Shiroe, edit message, 28 Sept).
   * Optional so a caller that builds a set by hand (tests) can leave it out;
   * missing reads as null.
   */
  onEdit?: (() => void) | null;
  /** Null unless the reader wrote the message (every room kind, admins too). */
  onDelete: (() => void) | null;
}

function PinItem({
  pin,
  className,
  onDone,
}: {
  pin: NonNullable<MessageActionSet['pin']>;
  className: string;
  onDone: () => void;
}) {
  if (pin.pinned) {
    return (
      <button
        type="button"
        role="menuitem"
        className={className}
        onClick={() => {
          onDone();
          pin.onToggle();
        }}
      >
        📌 Unpin message
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        role="menuitem"
        className={className}
        disabled={pin.full}
        aria-disabled={pin.full}
        onClick={() => {
          onDone();
          pin.onToggle();
        }}
      >
        📌 Pin message
      </button>
      {pin.full ? <p className="message-actions-hint">{PIN_FULL_HINT}</p> : null}
    </>
  );
}

/** The three-dot menu, in the order the design fixes. */
function MoreMenu({ actions, onClose }: { actions: MessageActionSet; onClose: () => void }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    rootRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div ref={rootRef} className="message-actions-menu" role="menu" aria-label="More actions">
      <button
        type="button"
        role="menuitem"
        className="message-actions-menu-item"
        onClick={() => {
          onClose();
          actions.onMakeTask();
        }}
      >
        ☑ Make a task
      </button>
      {actions.pin === null ? null : (
        <PinItem pin={actions.pin} className="message-actions-menu-item" onDone={onClose} />
      )}
      {(actions.onEdit ?? null) === null ? null : (
        <button
          type="button"
          role="menuitem"
          className="message-actions-menu-item"
          onClick={() => {
            onClose();
            actions.onEdit?.();
          }}
        >
          ✎ Edit message
        </button>
      )}
      {actions.onDelete === null ? null : (
        <>
          <hr className="message-actions-rule" />
          <button
            type="button"
            role="menuitem"
            className="message-actions-menu-item message-actions-menu-item--danger"
            onClick={() => {
              onClose();
              actions.onDelete?.();
            }}
          >
            🗑 Delete message
          </button>
        </>
      )}
    </div>
  );
}

/** The hover bar. CSS shows it on hover and focus, and hides it on touch screens. */
export function MessageActionBar({ actions }: { actions: MessageActionSet }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="message-actions" role="toolbar" aria-label="Message actions">
      {actions.onReply === null ? null : (
        <button type="button" className="message-actions-button" onClick={actions.onReply}>
          ↩ Reply
        </button>
      )}
      {actions.onForward === null ? null : (
        <button type="button" className="message-actions-button" onClick={actions.onForward}>
          ↪ Forward
        </button>
      )}
      <button
        type="button"
        className="message-actions-button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        ⋯
      </button>
      {menuOpen ? <MoreMenu actions={actions} onClose={() => setMenuOpen(false)} /> : null}
    </div>
  );
}

/**
 * The phone sheet (design-note "Phone"): quick reactions on top, then Reply,
 * Forward, Make a task, Pin or Unpin, and Edit and Delete on your own messages.
 */
export function MessageActionSheet({
  actions,
  reactions,
  onClose,
}: {
  actions: MessageActionSet;
  reactions: {
    list: ReactionSummary[] | undefined;
    canReact: boolean;
    onToggle: (emoji: string, mine: boolean) => void;
  };
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const run = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <>
      <div className="message-sheet-scrim" onClick={onClose} aria-hidden="true" />
      <div className="message-sheet" role="dialog" aria-modal="true" aria-label="Message actions">
        {reactions.canReact ? (
          <div className="message-sheet-reactions">
            {QUICK_REACTIONS.slice(0, 6).map((emoji) => {
              const mine = reactions.list?.some((entry) => entry.emoji === emoji && entry.mine);
              return (
                <button
                  key={emoji}
                  type="button"
                  className="message-sheet-reaction"
                  aria-label={`React with ${reactionName(emoji)}`}
                  aria-pressed={mine === true}
                  onClick={run(() => reactions.onToggle(emoji, mine === true))}
                >
                  <AnimatedEmoji emoji={emoji} />
                </button>
              );
            })}
          </div>
        ) : null}
        {actions.onReply === null ? null : (
          <button
            type="button"
            role="menuitem"
            className="message-sheet-item"
            onClick={run(actions.onReply)}
          >
            ↩ Reply
          </button>
        )}
        {actions.onForward === null ? null : (
          <button
            type="button"
            role="menuitem"
            className="message-sheet-item"
            onClick={run(actions.onForward)}
          >
            ↪ Forward
          </button>
        )}
        <button
          type="button"
          role="menuitem"
          className="message-sheet-item"
          onClick={run(actions.onMakeTask)}
        >
          ☑ Make a task
        </button>
        {actions.pin === null ? null : (
          <PinItem pin={actions.pin} className="message-sheet-item" onDone={onClose} />
        )}
        {actions.onEdit === undefined || actions.onEdit === null ? null : (
          <button
            type="button"
            role="menuitem"
            className="message-sheet-item"
            onClick={run(actions.onEdit)}
          >
            ✎ Edit message
          </button>
        )}
        {actions.onDelete === null ? null : (
          <button
            type="button"
            role="menuitem"
            className="message-sheet-item message-sheet-item--danger"
            onClick={run(actions.onDelete)}
          >
            🗑 Delete message
          </button>
        )}
      </div>
    </>
  );
}

/**
 * Whether this screen has no hover (a phone). jsdom and old browsers have no
 * matchMedia; they count as desktop, where the bar is the way in.
 */
export function isTouchScreen(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
}
